import type { MaybeRefOrGetter } from "vue";

const TWO_PART_TLD = /\.(co|com|org|net|gov|edu|ac)\.[a-z]{2}$/;

function apexOf(hostname: string): string | null {
    const parts = hostname.split(".");

    if (parts.length <= 2) return null;

    const labels = TWO_PART_TLD.test(hostname) ? 3 : 2;
    const apex = parts.slice(-labels).join(".");

    return apex === hostname ? null : apex;
}

function faviconUrl(domain: string): string {
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
}

/** Google serves a 16px default globe for unknown domains, so anything under 32px is a miss. */
function probeFavicon(url: string): Promise<boolean> {
    return new Promise((resolve) => {
        const image = new Image();

        image.onload = () => resolve(image.naturalWidth >= 32);
        image.onerror = () => resolve(false);
        image.src = url;
    });
}

export async function resolveDomainFavicon(
    host: string,
): Promise<string | null> {
    const candidates = [host, apexOf(host)].filter(
        (candidate): candidate is string => !!candidate,
    );

    for (const candidate of candidates) {
        const candidateUrl = faviconUrl(candidate);

        if (await probeFavicon(candidateUrl)) {
            return candidateUrl;
        }
    }

    return null;
}

const domainFaviconCache = reactive(new Map<string, string | null>());

/** Reactive lookup that stays null until the favicon probe for the host resolves. */
export function cachedDomainFavicon(
    host: string | null | undefined,
): string | null {
    if (!host || host.includes("{")) return null;

    if (!domainFaviconCache.has(host)) {
        domainFaviconCache.set(host, null);

        if (import.meta.client) {
            resolveDomainFavicon(host).then((url) => {
                if (url) domainFaviconCache.set(host, url);
            });
        }
    }

    return domainFaviconCache.get(host) ?? null;
}

export function fieldAvatarSrc(field: {
    metadata?: Record<string, unknown> | null;
}): string | null {
    if (field.metadata?.avatar && typeof field.metadata.avatar === "object") {
        const src = (field.metadata.avatar as { src?: unknown }).src;

        if (typeof src === "string" && src) {
            return src;
        }
    }

    return (
        ((field.metadata?.avatar as string | undefined) ||
            cachedDomainFavicon(
                field.metadata?.avatar_domain as string | undefined,
            )) ??
        null
    );
}

export function useDomainFavicon(
    url: MaybeRefOrGetter<string | null | undefined>,
) {
    const host = computed(() => {
        try {
            return new URL(toValue(url) ?? "").hostname || null;
        } catch {
            return null;
        }
    });

    const favicon = ref<string | null>(null);

    watch(
        host,
        async (current) => {
            favicon.value = null;

            if (!current || !import.meta.client) return;

            const resolved = await resolveDomainFavicon(current);

            if (host.value === current) {
                favicon.value = resolved;
            }
        },
        { immediate: true },
    );

    return { host, favicon };
}
